import React, { useEffect, useState } from 'react';
import { FormInitialValues } from 'src/components/CustomisationPage/pages/Configure';
import { useFieldValue } from 'src/core/forms';
import {
    ApplicationMethod,
    ApplicationType,
    Fields,
    methodFromLocations,
    ProductLocation,
} from 'src/components/CustomisationPage/feature/customisation.types';
import { useFormApi } from 'src/core/forms';
import { ConfigureFields } from 'src/components/CustomisationPage/steps/ConfigureFields';
import { MethodFieldset } from 'src/components/CustomisationPage/steps/MethodFieldSet';
import { TypeFieldset } from 'src/components/CustomisationPage/steps/TypeFieldSet';
import { PositionFieldset, PositionValidationSingle } from 'src/components/CustomisationPage/steps/PositionFieldSet';
import { BackgroundStyles } from 'src/components/BundleProduct/MultipleStepCustomisation/customisations.types';

import { BundleArtworkSelect } from '../Artwork/BundleArtworkSelect';

import classes from './ProductBundle.scss';
import { ARTWORK_FORM_LOCATION_PREFIX, ARTWORK_FORM_PREFIX, BUNDLE_ARTWORK_FIELD } from './bundle-product.types';

type Props = {
    initialValues: FormInitialValues;
    locations: ProductLocation[];
    fieldIndex: number;
    numberOfArtworks?: number;
    recommendedApplication?: ApplicationMethod;
};

export const BundleCustomisation: React.FC<Props> = React.memo((props) => {
    const { fieldIndex, initialValues, locations, numberOfArtworks, recommendedApplication } = props;
    const api = useFormApi();

    const artwork = useFieldValue(`${fieldIndex}.${BUNDLE_ARTWORK_FIELD}`);
    const addCustomisation = artwork !== 'none';
    const usingNew = artwork === 'new';

    const field = `${fieldIndex}.${ARTWORK_FORM_LOCATION_PREFIX}`;
    const selectedLocation = useFieldValue(field);
    const applicationMethodsAvailable = selectedLocation && methodFromLocations(locations, selectedLocation);

    useEffect(() => {
        if (applicationMethodsAvailable) {
            if (applicationMethodsAvailable === ApplicationMethod.Both) return;
            api.setValues({
                [`${fieldIndex}.${'bundle-customisation.applicationMethod'}`]: String(applicationMethodsAvailable),
            });
        }
    }, [api, applicationMethodsAvailable, fieldIndex]);

    const [applicationMethodId, setApplicationMethodId] = useState('');

    return (
        <div>
            <h3 className={classes.subHeader}>Choose position</h3>
            <BundlePositionSelect isBundle={true} locations={locations} fieldIndex={fieldIndex} />
            {applicationMethodsAvailable && (
                <>
                    <div className={`${fieldIndex !== 1 ? classes.hideMethods : ''}`}>
                        <h3 className={classes.subHeader}>Choose application method</h3>
                        <MethodFieldset
                            fieldPrefix={`${fieldIndex}.${ARTWORK_FORM_PREFIX}`}
                            applicationMethodFromLocations={applicationMethodsAvailable}
                            recommendedApplication={recommendedApplication}
                            fieldIndex={fieldIndex}
                            numberOfArtworks={numberOfArtworks}
                            applicationMethodId={applicationMethodId}
                            setApplicationMethodId={setApplicationMethodId}
                        />
                    </div>
                    <h3 className={classes.subHeader}>Choose artwork</h3>

                    <BundleArtworkSelect fieldIndex={fieldIndex} locations={props.locations} />
                </>
            )}
            {addCustomisation && (
                <>
                    {usingNew && (
                        <>
                            <h3 className={classes.subHeader}>Choose application type</h3>
                            <TypeFieldset
                                isBundleView={true}
                                fieldPrefix={`${fieldIndex}.${ARTWORK_FORM_PREFIX}`}
                                fieldIndex={fieldIndex}
                            />
                            <h3 className={classes.subHeader}>Add your logo</h3>
                            <ConfigureSelector initialValues={initialValues} fieldIndex={fieldIndex} />
                        </>
                    )}
                    <PositionValidationSingle fieldIndex={fieldIndex} />
                </>
            )}
        </div>
    );
});

const ConfigureSelector: React.FC<{ initialValues: FormInitialValues; fieldIndex?: number }> = ({
    initialValues,
    fieldIndex,
}) => {
    const field = `${fieldIndex}.${ARTWORK_FORM_PREFIX}${Fields.applicationType}`;
    const applicationType = useFieldValue(field) === 'logo' ? ApplicationType.Logo : ApplicationType.Text;
    return (
        <div style={{ minHeight: fieldIndex && fieldIndex <= 1 ? '30rem' : '54rem' }}>
            <ConfigureFields
                applicationType={applicationType}
                initialValues={initialValues}
                fieldPrefix={`${fieldIndex}.${ARTWORK_FORM_PREFIX}`}
                fieldIndex={fieldIndex}
                isBundleView={true}
            />
        </div>
    );
};

export const BundlePositionSelect: React.FC<{
    locations: ProductLocation[];
    isBundle: boolean;
    fieldIndex?: number;
    selectPosition?: (position) => void;
    oneOptionSelect?: boolean;
    backgroundStyles?: BackgroundStyles;
    avoidedSelectedPosition?: string;
    freeCustomisation?: boolean;
}> = (props) => {
    const {
        locations,
        isBundle,
        fieldIndex,
        oneOptionSelect,
        backgroundStyles,
        avoidedSelectedPosition,
        freeCustomisation,
    } = props;

    return (
        <PositionFieldset
            oneOptionSelect={oneOptionSelect}
            isBundle={isBundle}
            locations={locations}
            fieldIndex={fieldIndex}
            alreadyUsedLocations={[]}
            backgroundStyles={backgroundStyles}
            avoidedSelectedPosition={avoidedSelectedPosition}
            freeCustomisation={freeCustomisation}
        />
    );
};
